import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import '../style/Chat.css';
import fetchNodeChat, { getMessagesByTopic } from '../helpers/fetchNodeChat'
import sendFileChatMessage from '../helpers/sendFileChatMessage'
import fetchChatHistory from '../helpers/fetchChatHistory'
import MarkdownRenderer from './MarkdownRenderer';
import { notifyAndInviteToSubscribe } from './notifyAndInviteToSubscribe';
import { IoBookOutline } from "react-icons/io5";

// Citation trigger (button to expand/collapse references)
const CitationTrigger = ({ references, expanded, onToggle }) => {
  const { t } = useTranslation();
  if (!references || references.length === 0) return null;
  return (
    <button
      type="button"
      className="citation-trigger"
      onClick={(e) => { e.preventDefault(); onToggle?.(); }}
      aria-label={t('chat.viewCitations')}
      aria-expanded={expanded}
    >
      <IoBookOutline />
      <span className="citation-count">{references.length}</span>
    </button>
  );
};

// Citation panel (collapsible content between message and timestamp)
const CitationPanel = ({ references, expanded }) => {
  const { t } = useTranslation();
  if (!references || references.length === 0 || !expanded) return null;
  return (
    <div className="citation-panel" role="region" aria-label={t('chat.references')}>
      {references.map((ref, idx) => (
        <div key={idx} className="citation-item">
          <span className="citation-page">{t('chat.page', { page: ref.page })}</span>
          <span className="citation-text">"{ref.citation}"</span>
        </div>
      ))}
    </div>
  );
};

const Chat = ({ topic, subject, notice, setShowLoginModal, setLevelUpCardGame = null, fileId, initialChatId, onChatIdCreated, onClose }) => {
  const initialized = useRef(false);
  const messagesContainerBottom = useRef(null);
  const scrollTimeoutRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [messages, setMessages] = useState(() => fileId ? [] : getMessagesByTopic(subject));
  const [inputMessage, setInputMessage] = useState('');
  const [chatId, setChatId] = useState(initialChatId || null);
  const [expandedRefsIndex, setExpandedRefsIndex] = useState(null);
  const { t } = useTranslation();

  const loadChatHistory = useCallback(async () => {
    if (initialChatId) {
      setIsLoading(true);
      try {
        const historyMessages = await fetchChatHistory(initialChatId);
        setMessages(historyMessages);
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
        if (error.status === 429) {
          notifyAndInviteToSubscribe(notice, error, setShowLoginModal);
          onClose?.();
        } else {
          throw error;
        }
      }
    } else {
      setMessages([
        { role: 'assistant', content: t('chat.welcomeMessage'), timestamp: new Date() }
      ]);
    }
  }, [initialChatId, notice, setShowLoginModal, onClose, t]);

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    loadChatHistory();
    setInputMessage('');
  }, [loadChatHistory]);

  const handleSendMessage = useCallback(async (messageText = null) => {
    // Prevent multiple simultaneous sends
    if (isLoading) return;

    const textToSend = messageText || inputMessage;
    if (!textToSend.trim()) return;

    const trimmed = textToSend.trim();

    const userMessage = {
      role: 'user',
      content: trimmed,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      let returnedMessages, _id;

      // Use file chat if fileId is present
      if (fileId) {
        ({ messages: returnedMessages, _id } = await sendFileChatMessage(userMessage, fileId, chatId, subject));
      } else {
        // Use node chat for topic nodes
        ({ messages: returnedMessages, _id } = await fetchNodeChat(userMessage, messages.at(-1), subject, topic, chatId, null));
      }

      // Add returned messages from the backend
      if (returnedMessages && Array.isArray(returnedMessages) && returnedMessages.length > 0) {
        const assistantMessages = returnedMessages.filter(msg => msg.role === 'assistant');
        if (assistantMessages.length > 0) {
          setMessages((prev) => [...prev, ...assistantMessages]);
        }
      }

      setChatId(_id);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      // Remove the user message we just added
      setMessages((prev) => prev.slice(0, -1));

      if (error.status === 429) {
        notifyAndInviteToSubscribe(notice, error, setShowLoginModal);
        onClose?.();
      } else {
        // For other errors, add error message to chat
        setMessages((prev) => [...prev, {
          role: 'assistant',
          content: t('chat.errorMessage'),
          timestamp: new Date()
        }]);
      }
    }
  }, [inputMessage, messages, subject, topic, notice, setShowLoginModal, chatId, fileId, onClose, isLoading, t]);

  const scrollToBottom = useCallback(() => {
    messagesContainerBottom.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    // Clear any pending scroll timeout
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    // Debounce scroll calls - only scroll once after a small delay
    // This prevents multiple scrolls when messages update rapidly
    scrollTimeoutRef.current = setTimeout(() => {
      scrollToBottom();
    }, 100);

    // Cleanup timeout on unmount
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [messages, scrollToBottom]);

  const handleInputChange = (e) => {
    setInputMessage(e.target.value);
  };

  return (
    <div className='chat-parent'>
      <div className="chat-container">
        <div className="chat-header-bar">
          <div className="chat-header-title">{subject}</div>
        </div>
        <div className="messages-container">
          {messages.map((message, index) => (
            <div key={index} className="message-wrapper">
              <div
                className={`message ${message.role === 'user' ? 'message-user' : 'message-bot'}`}
              >
                <div className="message-content">
                  <MarkdownRenderer>
                    {message.content}
                  </MarkdownRenderer>
                  {message.role === 'assistant' && message.references?.length > 0 && (
                    <CitationTrigger
                      references={message.references}
                      expanded={expandedRefsIndex === index}
                      onToggle={() => setExpandedRefsIndex((prev) => (prev === index ? null : index))}
                    />
                  )}
                </div>
              </div>
              {message.role === 'assistant' && message.references?.length > 0 && (
                <CitationPanel references={message.references} expanded={expandedRefsIndex === index} />
              )}
              <small className="timestamp">{new Date(message.timestamp).toLocaleTimeString()}</small>
            </div>
          ))}
          {isLoading && <div className="loading">{t('chat.typingIndicator')}</div>}
          <div ref={messagesContainerBottom} />
        </div>
        <div className="input-container">
          <input
            type="text"
            placeholder={t('chat.inputPlaceholder')}
            value={inputMessage}
            onChange={handleInputChange}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSendMessage();
            }}
          />
        </div>
      </div>
      <button 
        className="navbar-item mt-2" 
        disabled={isLoading}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (!isLoading) {
            handleSendMessage();
          }
        }}
      >
        {t('common.send')}
      </button>
    </div>
  );
};

export default Chat;
